/**
 * BatchImportDialog — three-step dialog for multi-record PitchBook import.
 *
 *  Step 1 (review):  one collapsible row per company with verdict badge, field
 *                    counts, person selection, and per-field override dropdowns.
 *  Step 2 (progress): live progress bar polling GET /runs/{run_id}, Cancel button.
 *  Step 3 (result):  aggregated stats, per-company links list, inline Undo.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    X, Loader2, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight,
    Building2, Landmark, User, Undo2, XCircle, PackageOpen,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
    DiffTable, ImportResultLinks, PersonRow, VerdictBadge,
    pbValue, defaultDecision, willWrite,
} from './ImportDiffParts';
import { isEmpty } from '../../utils/pitchbookDiff';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 1500;

// ─── helpers ──────────────────────────────────────────────────────────────────

function countWrites(fields, decisions) {
    let writes = 0;
    for (const f of fields || []) {
        if (!f.target || f.policy === 'pitchbook_tab_only' || isEmpty(pbValue(f))) continue;
        const d = decisions[f.csv_column] ?? defaultDecision(f);
        if (willWrite(f, d)) writes++;
    }
    return writes;
}

function EntityIcon({ entity, className }) {
    if (entity === 'investor') return <Landmark className={className} />;
    return <Building2 className={className} />;
}

// ─── per-company review row ───────────────────────────────────────────────────

function CompanyReviewRow({
    block, displayName, included, onToggleInclude,
    expanded, onToggleExpand,
    selectedPersons, onPersonToggle, onAllPersons,
    expandedPersons, onPersonExpand,
    companyDecisions, onCompanyDecision,
    personDecisions, onPersonDecision,
}) {
    const company = block.company;
    const persons = block.persons || [];
    const entity = block.entity;
    const pbId = block.pitchbook_id;
    const selectedCount = persons.filter((p) => !!selectedPersons[p.pitchbook_id]).length;
    const compWrites = useMemo(
        () => countWrites(company.fields, companyDecisions), [company.fields, companyDecisions]);
    const isUpdate = company.verdict === 'update' || company.verdict === 'skip_no_change';

    return (
        <div className={`border rounded-xl overflow-hidden transition-all ${included ? 'border-stone-200' : 'border-stone-100 opacity-60'}`}>
            {/* Summary bar */}
            <div className="flex items-center gap-3 px-4 py-3 bg-stone-50 border-b border-stone-100">
                <input
                    type="checkbox"
                    checked={!!included}
                    onChange={(e) => onToggleInclude(e.target.checked)}
                    className="rounded border-stone-300 text-stone-900 focus:ring-stone-700 shrink-0"
                />
                <EntityIcon entity={entity} className="w-4 h-4 text-stone-400 shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-800 text-sm truncate" title={displayName}>
                        {displayName}
                        <span className="ml-2 text-[10px] text-stone-400 font-mono font-normal">{block.pitchbook_id}</span>
                    </p>
                    {company.matched_crm_account && (
                        <p className="text-xs text-stone-400 truncate">→ {company.matched_crm_account.name}</p>
                    )}
                </div>
                <VerdictBadge verdict={company.verdict} />
                <div className="flex items-center gap-3 text-xs text-stone-500 shrink-0">
                    <span>{compWrites} field{compWrites === 1 ? '' : 's'}</span>
                    {persons.length > 0 && (
                        <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {selectedCount}/{persons.length}
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onToggleExpand}
                    className="text-stone-400 hover:text-stone-700 shrink-0"
                    title={expanded ? 'Collapse' : 'Expand diff'}
                >
                    {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
            </div>

            {expanded && included && (
                <div className="p-4 space-y-4">
                    {/* Company diff */}
                    <div>
                        <p className="text-xs font-medium text-stone-500 mb-2">Company fields</p>
                        <DiffTable
                            fields={company.fields}
                            decisions={companyDecisions}
                            onDecision={onCompanyDecision}
                            isUpdate={isUpdate}
                        />
                    </div>

                    {/* People */}
                    {persons.length > 0 && (
                        <div>
                            <div className="flex items-center justify-between gap-3 mb-2">
                                <div className="flex items-center gap-2">
                                    <User className="w-3.5 h-3.5 text-stone-400" />
                                    <p className="text-xs font-medium text-stone-500">
                                        People ({selectedCount}/{persons.length} selected)
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                    <button type="button" onClick={() => onAllPersons(pbId, true)} className="text-stone-600 hover:text-stone-900">All</button>
                                    <span className="text-stone-300">·</span>
                                    <button type="button" onClick={() => onAllPersons(pbId, false)} className="text-stone-600 hover:text-stone-900">None</button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                {persons.map((p) => (
                                    <PersonRow
                                        key={p.pitchbook_id}
                                        person={p}
                                        selected={!!selectedPersons[p.pitchbook_id]}
                                        expanded={!!expandedPersons[p.pitchbook_id]}
                                        onToggleSelect={(v) => onPersonToggle(pbId, p.pitchbook_id, v)}
                                        onToggleExpand={() => onPersonExpand(pbId, p.pitchbook_id)}
                                        decisions={personDecisions[p.pitchbook_id] || {}}
                                        onDecision={(col, pol) => onPersonDecision(pbId, p.pitchbook_id, col, pol)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Progress bar ─────────────────────────────────────────────────────────────

function ProgressBar({ done, total }) {
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return (
        <div className="w-full bg-stone-100 rounded-full h-2 overflow-hidden">
            <div
                className="bg-stone-800 h-2 rounded-full transition-all duration-500"
                style={{ width: `${pct}%` }}
            />
        </div>
    );
}

// ─── Main dialog ──────────────────────────────────────────────────────────────

export default function BatchImportDialog({ pitchbookIds, selectionMeta = {}, onClose, onCommitted }) {
    const { getAuthHeader } = useAuth();

    // ── step: 'loading' | 'review' | 'progress' | 'result' | 'error'
    const [step, setStep] = useState('loading');
    const [preview, setPreview] = useState(null);     // batch preview response
    const [previewError, setPreviewError] = useState(null);

    // Per-company state keyed by pitchbook_id
    const [included, setIncluded] = useState({});          // {[pbId]: bool}
    const [expanded, setExpanded] = useState({});          // {[pbId]: bool}
    const [selectedPersons, setSelectedPersons] = useState({});  // {[pbId]: {[personId]: bool}}
    const [expandedPersons, setExpandedPersons] = useState({});  // {[pbId]: {[personId]: bool}}
    const [companyDecisions, setCompanyDecisions] = useState({}); // {[pbId]: {[col]: policy}}
    const [personDecisions, setPersonDecisions] = useState({});   // {[pbId]: {[personId]: {[col]: policy}}}

    // Run tracking
    const [runId, setRunId] = useState(null);
    const [runStatus, setRunStatus] = useState(null);   // latest run doc
    const [cancelling, setCancelling] = useState(false);
    const [undoing, setUndoing] = useState(false);
    const [undone, setUndone] = useState(false);
    const pollRef = useRef(null);

    // ── load preview on mount ────────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            setStep('loading');
            setPreviewError(null);
            try {
                const res = await axios.post(
                    `${API}/pitchbook/manual-sync/batch-preview`,
                    { pitchbook_ids: pitchbookIds },
                    { headers: getAuthHeader() },
                );
                const data = res.data;
                setPreview(data);

                // Initialise per-company state.
                const inc = {};
                const selP = {};
                const expP = {};
                for (const block of data.companies || []) {
                    const pbId = block.pitchbook_id;
                    inc[pbId] = true;
                    selP[pbId] = Object.fromEntries(
                        (block.persons || []).map((p) => [p.pitchbook_id, true]));
                    expP[pbId] = {};
                }
                setIncluded(inc);
                setSelectedPersons(selP);
                setExpandedPersons(expP);
                setStep('review');
            } catch (e) {
                const detail = e.response?.data?.detail;
                setPreviewError(typeof detail === 'string' ? detail : 'Failed to load batch preview.');
                setStep('error');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── polling ──────────────────────────────────────────────────────────────
    const stopPolling = useCallback(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }, []);

    const startPolling = useCallback((id) => {
        stopPolling();
        pollRef.current = setInterval(async () => {
            try {
                const res = await axios.get(`${API}/pitchbook/runs/${id}`, { headers: getAuthHeader() });
                const run = res.data;
                setRunStatus(run);
                if (['completed', 'cancelled', 'failed'].includes(run.status)) {
                    stopPolling();
                    setStep('result');
                }
            } catch { /* ignore transient errors */ }
        }, POLL_INTERVAL_MS);
    }, [getAuthHeader, stopPolling]);

    useEffect(() => () => stopPolling(), [stopPolling]);

    // ── commit ───────────────────────────────────────────────────────────────
    const handleCommit = async () => {
        const companies = (preview?.companies || []).filter((b) => included[b.pitchbook_id] !== false);
        const selections = companies.map((block) => {
            const pbId = block.pitchbook_id;
            const selPersMap = selectedPersons[pbId] || {};
            const myCompDecisions = companyDecisions[pbId] || {};
            const myPersonDecisions = personDecisions[pbId] || {};

            // Only ship decisions that deviate from the mapping default.
            const companyOverrides = {};
            for (const [col, policy] of Object.entries(myCompDecisions)) {
                const row = (block.company.fields || []).find((f) => f.csv_column === col);
                if (row && policy !== defaultDecision(row)) companyOverrides[col] = policy;
            }
            const personsOverrides = {};
            for (const p of block.persons || []) {
                const pid = p.pitchbook_id;
                const pDecisions = myPersonDecisions[pid] || {};
                const out = {};
                for (const [col, policy] of Object.entries(pDecisions)) {
                    const row = (p.fields || []).find((f) => f.csv_column === col);
                    if (row && policy !== defaultDecision(row)) out[col] = policy;
                }
                if (Object.keys(out).length) personsOverrides[pid] = out;
            }

            return {
                company_pb_id: pbId,
                include_company: true,
                selected_person_ids: Object.entries(selPersMap)
                    .filter(([, v]) => v).map(([pid]) => pid),
                field_overrides: { company: companyOverrides, persons: personsOverrides },
            };
        });

        if (!selections.length) {
            toast.error('No companies selected to import');
            return;
        }

        try {
            const res = await axios.post(
                `${API}/pitchbook/manual-sync/batch-commit`,
                { preview_id: preview.preview_id, selections },
                { headers: getAuthHeader() },
            );
            const id = res.data.run_id;
            setRunId(id);
            setStep('progress');
            startPolling(id);
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Failed to start import');
        }
    };

    // ── cancel ───────────────────────────────────────────────────────────────
    const handleCancel = async () => {
        if (!runId) return;
        const ok = window.confirm(
            'Stop this import?\n\nAny records already written will be kept. You can use Undo afterward to revert the partial import.');
        if (!ok) return;
        setCancelling(true);
        try {
            await axios.post(`${API}/pitchbook/runs/${runId}/cancel`, {}, { headers: getAuthHeader() });
            // Polling will detect the "cancelled" status and move to result step.
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to cancel');
        } finally {
            setCancelling(false);
        }
    };

    // ── undo ─────────────────────────────────────────────────────────────────
    const handleUndo = async () => {
        if (!runId) return;
        const count = runStatus?.operations?.length || 0;
        const ok = window.confirm(
            `Undo this batch import?\n\nThis will soft-delete records created and restore fields updated.\nJournaled operations: ${count}`);
        if (!ok) return;
        setUndoing(true);
        try {
            await axios.post(`${API}/pitchbook/runs/${runId}/undo`, {}, { headers: getAuthHeader() });
            setUndone(true);
            toast.success('Batch import reverted');
            onCommitted?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to revert');
        } finally {
            setUndoing(false);
        }
    };

    // ── person helpers ────────────────────────────────────────────────────────
    const togglePerson = (pbId, personId, v) =>
        setSelectedPersons((s) => ({ ...s, [pbId]: { ...(s[pbId] || {}), [personId]: v } }));
    const toggleAllPersons = (pbId, v) =>
        setSelectedPersons((s) => {
            const block = (preview?.companies || []).find((b) => b.pitchbook_id === pbId);
            const map = Object.fromEntries((block?.persons || []).map((p) => [p.pitchbook_id, v]));
            return { ...s, [pbId]: map };
        });
    const togglePersonExpand = (pbId, personId) =>
        setExpandedPersons((s) => ({
            ...s, [pbId]: { ...(s[pbId] || {}), [personId]: !(s[pbId] || {})[personId] },
        }));
    const setCompanyDecision = (pbId, col, policy) =>
        setCompanyDecisions((s) => ({ ...s, [pbId]: { ...(s[pbId] || {}), [col]: policy } }));
    const setPersonDecision = (pbId, personId, col, policy) =>
        setPersonDecisions((s) => ({
            ...s,
            [pbId]: {
                ...(s[pbId] || {}),
                [personId]: { ...((s[pbId] || {})[personId] || {}), [col]: policy },
            },
        }));

    // ── summary counts ─────────────────────────────────────────────────────────
    const summary = useMemo(() => {
        if (!preview) return { companies: 0, persons: 0 };
        let companies = 0;
        let persons = 0;
        for (const block of preview.companies || []) {
            const pbId = block.pitchbook_id;
            if (!included[pbId]) continue;
            companies++;
            persons += Object.values(selectedPersons[pbId] || {}).filter(Boolean).length;
        }
        return { companies, persons };
    }, [preview, included, selectedPersons]);

    const progress = runStatus?.progress || {};

    // ── render ─────────────────────────────────────────────────────────────────
    return createPortal((
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 shrink-0">
                    <div className="min-w-0">
                        <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {step === 'loading' ? 'Preparing batch preview…' :
                             step === 'review'   ? `Import ${pitchbookIds.length} record${pitchbookIds.length === 1 ? '' : 's'} to CRM` :
                             step === 'progress' ? 'Importing to CRM…' :
                             step === 'result'   ? 'Import complete' :
                             'Preview error'}
                        </h3>
                        {step === 'review' && (
                            <p className="text-xs text-stone-500 mt-0.5">
                                Review each record, adjust per-field decisions if needed, then confirm.
                            </p>
                        )}
                    </div>
                    {step !== 'progress' && (
                        <button onClick={onClose} className="text-stone-400 hover:text-stone-700 shrink-0 ml-3">
                            <X className="w-5 h-5" />
                        </button>
                    )}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5 min-h-0">

                    {/* Loading */}
                    {step === 'loading' && (
                        <div className="py-16 text-center text-stone-400">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                            <p className="text-xs">Building previews for {pitchbookIds.length} record{pitchbookIds.length === 1 ? '' : 's'}…</p>
                        </div>
                    )}

                    {/* Error */}
                    {step === 'error' && (
                        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                            <p className="text-sm text-amber-800">{previewError}</p>
                        </div>
                    )}

                    {/* Review */}
                    {step === 'review' && preview && (
                        <div className="space-y-3">
                            {preview.warnings?.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                                    {preview.warnings.map((w, i) => <div key={i}>• {w}</div>)}
                                </div>
                            )}
                            {(preview.companies || []).map((block) => {
                                const pbId = block.pitchbook_id;
                                return (
                                    <CompanyReviewRow
                                        key={pbId}
                                        block={block}
                                        displayName={block.company?.name || selectionMeta[pbId]?.name || pbId}
                                        included={included[pbId] !== false}
                                        onToggleInclude={(v) => setIncluded((s) => ({ ...s, [pbId]: v }))}
                                        expanded={!!expanded[pbId]}
                                        onToggleExpand={() => setExpanded((s) => ({ ...s, [pbId]: !s[pbId] }))}
                                        selectedPersons={selectedPersons[pbId] || {}}
                                        onPersonToggle={togglePerson}
                                        onAllPersons={toggleAllPersons}
                                        expandedPersons={expandedPersons[pbId] || {}}
                                        onPersonExpand={togglePersonExpand}
                                        companyDecisions={companyDecisions[pbId] || {}}
                                        onCompanyDecision={(col, pol) => setCompanyDecision(pbId, col, pol)}
                                        personDecisions={personDecisions[pbId] || {}}
                                        onPersonDecision={setPersonDecision}
                                    />
                                );
                            })}
                        </div>
                    )}

                    {/* Progress */}
                    {step === 'progress' && (
                        <div className="py-10 flex flex-col items-center gap-6 max-w-md mx-auto">
                            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center">
                                <Loader2 className="w-7 h-7 text-stone-600 animate-spin" />
                            </div>
                            <div className="w-full space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-stone-600 font-medium">
                                        {progress.companies_done ?? 0} of {progress.companies_total ?? pitchbookIds.length} companies
                                    </span>
                                    <span className="text-stone-400 text-xs">
                                        {progress.companies_total > 0
                                            ? Math.round(((progress.companies_done ?? 0) / progress.companies_total) * 100)
                                            : 0}%
                                    </span>
                                </div>
                                <ProgressBar
                                    done={progress.companies_done ?? 0}
                                    total={progress.companies_total ?? pitchbookIds.length}
                                />
                                {progress.current && (
                                    <p className="text-xs text-stone-400 truncate text-center">{progress.current}</p>
                                )}
                            </div>
                            <Button
                                variant="outline"
                                className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                disabled={cancelling}
                                onClick={handleCancel}
                            >
                                {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                                Stop import
                            </Button>
                            <p className="text-xs text-stone-400 text-center">
                                Records already written will be kept. You can undo after stopping.
                            </p>
                        </div>
                    )}

                    {/* Result */}
                    {step === 'result' && runStatus && (
                        <div className="py-8 space-y-5 text-center">
                            {undone ? (
                                <>
                                    <Undo2 className="w-12 h-12 text-stone-400 mx-auto" />
                                    <h4 className="font-semibold text-stone-800">Import reverted</h4>
                                    <p className="text-sm text-stone-500">All changes from this batch import were rolled back.</p>
                                </>
                            ) : runStatus.status === 'cancelled' ? (
                                <>
                                    <XCircle className="w-12 h-12 text-amber-400 mx-auto" />
                                    <h4 className="font-semibold text-stone-800">Import stopped</h4>
                                    <p className="text-sm text-stone-500">
                                        {(progress.companies_done ?? 0)} of {progress.companies_total ?? '?'} companies processed before the stop.
                                        Records already written remain in the CRM — use Undo to revert them.
                                    </p>
                                </>
                            ) : runStatus.status === 'failed' ? (
                                <>
                                    <AlertTriangle className="w-12 h-12 text-rose-400 mx-auto" />
                                    <h4 className="font-semibold text-stone-800">Import failed</h4>
                                    <p className="text-sm text-stone-500">
                                        An error occurred. Partial changes (if any) can be undone.
                                    </p>
                                    {runStatus.error && (
                                        <pre className="text-xs text-stone-500 bg-stone-50 rounded px-3 py-2 max-w-lg mx-auto overflow-auto text-left">{runStatus.error}</pre>
                                    )}
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
                                    <h4 className="font-semibold text-stone-800">Import completed</h4>
                                </>
                            )}

                            {/* Stats */}
                            {runStatus.projection_stats && !undone && (
                                <div className="inline-flex flex-col gap-1 text-sm text-stone-600">
                                    {Object.entries(runStatus.projection_stats).map(([entity, s]) => {
                                        if (typeof s !== 'object') return null;
                                        const { created = 0, updated = 0, skipped = 0 } = s;
                                        if (!created && !updated && !skipped) return null;
                                        return (
                                            <span key={entity}>
                                                {entity.charAt(0).toUpperCase() + entity.slice(1)}: {created} created · {updated} updated · {skipped} unchanged
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            {runStatus.warnings?.length > 0 && !undone && (
                                <div className="max-w-xl mx-auto bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 text-left">
                                    {runStatus.warnings.map((w, i) => <div key={i}>• {w}</div>)}
                                </div>
                            )}

                            {!undone && <ImportResultLinks links={runStatus.links || []} />}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-4 border-t border-stone-200 shrink-0">
                    <div className="text-xs text-stone-500">
                        {step === 'review' && (
                            <span>
                                {summary.companies} compan{summary.companies === 1 ? 'y' : 'ies'} · {summary.persons} person{summary.persons === 1 ? '' : 's'}
                            </span>
                        )}
                        {step === 'result' && !undone && (runStatus?.operations?.length || 0) > 0 && (
                            <button
                                onClick={handleUndo}
                                disabled={undoing}
                                className="flex items-center gap-1.5 text-stone-500 hover:text-rose-600"
                            >
                                {undoing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                                Undo this import
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        {(step === 'result' || step === 'error') && (
                            <Button onClick={() => { onClose(); if (step === 'result') onCommitted?.(); }}>
                                Done
                            </Button>
                        )}
                        {step === 'review' && (
                            <>
                                <Button variant="ghost" onClick={onClose}>Cancel</Button>
                                <Button
                                    disabled={summary.companies === 0}
                                    onClick={handleCommit}
                                >
                                    <PackageOpen className="w-4 h-4 mr-2" />
                                    Import {summary.companies} compan{summary.companies === 1 ? 'y' : 'ies'} + {summary.persons} people
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    ), document.body);
}
